<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  roleTemplate: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const initialRoleId = () => {
  if (props.roleTemplate) return `template_${props.roleTemplate}`;
  if (props.customRoleId) return `legacy_custom_${props.customRoleId}`;
  return props.type;
};
const selectedRoleId = ref(initialRoleId());
const agentCredentials = ref({ email: props.email });

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'template_doctor',
      name: 'template_doctor',
      roleTemplate: 'doctor',
      label: t('AGENT_MGMT.AGENT_TYPES.DOCTOR'),
    },
    {
      id: 'template_assistant',
      name: 'template_assistant',
      roleTemplate: 'assistant',
      label: t('AGENT_MGMT.AGENT_TYPES.ASSISTANT'),
    },
    {
      id: 'template_patient_coordinator',
      name: 'template_patient_coordinator',
      roleTemplate: 'patient_coordinator',
      label: t('AGENT_MGMT.AGENT_TYPES.PATIENT_COORDINATOR'),
    },
    {
      id: 'template_clinical_coordinator',
      name: 'template_clinical_coordinator',
      roleTemplate: 'clinical_coordinator',
      label: t('AGENT_MGMT.AGENT_TYPES.CLINICAL_COORDINATOR'),
    },
  ];

  if (!props.roleTemplate && !props.customRoleId) {
    defaultRoles.push({
      id: props.type,
      name: props.type,
      label:
        props.type === 'administrator'
          ? t('AGENT_MGMT.AGENT_TYPES.LEGACY_ADMINISTRATOR')
          : t('AGENT_MGMT.AGENT_TYPES.LEGACY_AGENT'),
    });
  }

  if (props.customRoleId) {
    defaultRoles.push({
      id: `legacy_custom_${props.customRoleId}`,
      name: 'legacy_custom_role',
      label: `${t('CUSTOM_ROLE.HEADER')} #${props.customRoleId}`,
      legacy: true,
    });
  }

  return defaultRoles;
});

const roleSelectOptions = computed(() =>
  roles.value.map(role => ({ value: role.id, label: role.label }))
);

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (selectedRole.value.roleTemplate) {
      payload.role_template = selectedRole.value.roleTemplate;
      payload.role =
        selectedRole.value.roleTemplate === 'doctor'
          ? 'administrator'
          : 'agent';
      payload.custom_role_id = null;
    } else if (!selectedRole.value.legacy) {
      payload.role = selectedRole.value.name;
      payload.role_template = null;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <Select
            v-model="selectedRoleId"
            :options="roleSelectOptions"
            :error="v$.selectedRoleId.$error ? 'error' : ''"
            full-width
            @change="v$.selectedRoleId.$touch"
          />
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <Select
            v-model="agentAvailability"
            :options="availabilityStatuses"
            :error="v$.agentAvailability.$error ? 'error' : ''"
            full-width
            @change="v$.agentAvailability.$touch"
          />
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
